import { useState, useRef, useEffect, useMemo } from 'react';
import { ChevronDown, Check, Search, X } from 'lucide-react';
import { classNames } from '@/lib/utils';

export interface MultiSelectOption {
  value: string;
  label: string;
}

interface MultiSelectProps {
  label: string;
  icon?: React.ElementType;
  options: MultiSelectOption[];
  selected: string[];
  onChange: (values: string[]) => void;
  searchable?: boolean;
  allLabel?: string;
}

export default function MultiSelect({
  label,
  icon: Icon,
  options,
  selected,
  onChange,
  searchable = false,
  allLabel = 'Todas',
}: MultiSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const filtered = useMemo(() => {
    if (!query.trim()) return options;
    const q = query.toLowerCase();
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query]);

  const allSelected = selected.length === 0 || selected.length === options.length;
  const filteredValues = filtered.map((o) => o.value);
  const allFilteredSelected = filteredValues.length > 0 && filteredValues.every((v) => selected.includes(v));

  const toggle = (value: string) => {
    onChange(selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value]);
  };

  const toggleAll = () => {
    if (allFilteredSelected) {
      onChange(selected.filter((v) => !filteredValues.includes(v)));
    } else {
      onChange([...new Set([...selected, ...filteredValues])]);
    }
  };

  const summary =
    selected.length === 0
      ? allLabel
      : selected.length === options.length
      ? allLabel
      : `${selected.length} seleccionada${selected.length > 1 ? 's' : ''}`;

  return (
    <div className="relative" ref={ref}>
      <label className="block text-xs font-semibold text-sereno-dark/60 mb-1">
        <span className="inline-flex items-center gap-1">
          {Icon && <Icon size={12} />}
          {label}
        </span>
      </label>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="input flex items-center justify-between gap-2 min-w-[160px] text-left"
      >
        <span className={classNames('truncate text-sm', allSelected ? 'text-sereno-dark/50' : 'text-sereno-dark')}>
          {summary}
        </span>
        <ChevronDown size={14} className={classNames('flex-shrink-0 transition-transform', open && 'rotate-180')} />
      </button>

      {open && (
        <div className="absolute z-30 mt-1 w-full min-w-[220px] bg-white rounded-lg shadow-lg border border-sereno-dark/10 max-h-72 overflow-hidden flex flex-col">
          {searchable && (
            <div className="p-2 border-b border-sereno-dark/10 sticky top-0 bg-white">
              <div className="relative">
                <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-sereno-dark/40" />
                <input
                  autoFocus
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Buscar..."
                  className="w-full pl-7 pr-6 py-1.5 text-sm rounded-md border border-sereno-dark/15 focus:outline-none focus:border-sereno-dark/40"
                />
                {query && (
                  <button onClick={() => setQuery('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-sereno-dark/40">
                    <X size={13} />
                  </button>
                )}
              </div>
            </div>
          )}
          <div className="overflow-y-auto">
            <button
              type="button"
              onClick={toggleAll}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm font-medium hover:bg-sereno-blue/20 border-b border-sereno-dark/5"
            >
              <span
                className={classNames(
                  'w-4 h-4 rounded border flex items-center justify-center flex-shrink-0',
                  allFilteredSelected ? 'bg-sereno-dark border-sereno-dark text-white an-active' : 'border-sereno-dark/30'
                )}
              >
                {allFilteredSelected && <Check size={11} />}
              </span>
              Seleccionar todo
            </button>
            {filtered.map((o) => {
              const isSel = selected.includes(o.value);
              return (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => toggle(o.value)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-sereno-blue/20 text-left"
                >
                  <span
                    className={classNames(
                      'w-4 h-4 rounded border flex items-center justify-center flex-shrink-0',
                      isSel ? 'bg-sereno-dark border-sereno-dark text-white an-active' : 'border-sereno-dark/30'
                    )}
                  >
                    {isSel && <Check size={11} />}
                  </span>
                  <span className="truncate">{o.label}</span>
                </button>
              );
            })}
            {filtered.length === 0 && (
              <p className="px-3 py-3 text-sm text-sereno-dark/40 text-center">Sin resultados</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
